// Cascade-suppressed Apply batch for Quick Settings (AC16; source for AC4, AC10, AC11).
//
// CRITICAL (AC16): the WHOLE batch AND each `block.setFieldValue(value, 'VALUE')` are wrapped in
// `Blockly.Events.disable()` / `Blockly.Events.enable()` inside `try/finally`. `disable()` bumps
// `Blockly.Events.disabled_` (blockly_node_javascript_en.js:934); `Events.fire` early-returns while
// it is non-zero, so NO change listener fires during Apply — including the unguarded tradeOptions
// onchange (tradeOptions.js:64 early-returns only on `ev.group==='BackwardCompatibility'`; its
// CHANGE branch at 109-139 has no ev.blockId guard and would otherwise call pollForContracts on
// every parented write). `try/finally` guarantees enable() always restores firing. We do NOT use
// `Blockly.Events.setGroup(false)` — it ends grouping but does NOT suppress firing.
//
// Nesting is safe: disabled_ is a counter, so the per-row wrap (disable++/enable--) inside the
// batch wrap (disable++/enable--) stays >= 1 throughout and returns to 0 exactly once at the end.
//
// Writes only ever target 'VALUE' (number_input/text_input) or 'NUM' (math_number), never a
// tradeOptions-watched field name, even if suppression ever regressed.

import { validateNumber, validateText, readBack } from './validation';

/* global Blockly */
const WRITABLE_FIELDS = ['VALUE', 'NUM']; // field-name guard — every mutation targets one of these.

const SUCCESS_BANNER = 'Quick Settings applied'; // AC14 success-notice literal (test-coupled).

/**
 * Apply a batch of tunable edits to the workspace with full cascade suppression.
 *
 * @param {Blockly.Workspace} workspace
 * @param {Array<{id:string, type:'number'|'text', field:'VALUE'|'NUM', value:string, min?:number, max?:number}>} edits
 * @returns {{applied:Array, errors:Array, banner:string, readBacks:Array}}
 *          applied — edits written; errors — edits skipped (missing block / invalid value);
 *          banner — form-level summary; readBacks — per-written-row AC11 read-back results.
 */
export const applyEdits = (workspace, edits) => {
    const applied = [];
    const errors = [];
    const readBacks = [];

    if (!workspace || !Array.isArray(edits) || edits.length === 0) {
        return { applied, errors, banner: errors.length ? '' : SUCCESS_BANNER, readBacks };
    }

    // ---- Whole-batch suppression (AC16): disable firing for the entire Apply, restore in finally.
    Blockly.Events.disable();
    try {
        edits.forEach(edit => {
            const { id, type, value } = edit;
            const field = edit.field || 'VALUE';

            // Per-row existence guard (AC10 partial): skip + record disposed/missing blocks.
            const block = typeof workspace.getBlockById === 'function' ? workspace.getBlockById(id) : null;
            if (!block || block.disposed) {
                errors.push({ id, error: 'Block no longer exists' });
                return;
            }
            if (!WRITABLE_FIELDS.includes(field)) {
                errors.push({ id, error: 'Field not editable' });
                return;
            }

            // Validate BEFORE write (AC5/AC6) — an invalid value never reaches setFieldValue.
            const constraints = type === 'number' ? { min: edit.min, max: edit.max } : {};
            const result = type === 'number' ? validateNumber(value, constraints) : validateText(value);
            if (!result.ok) {
                errors.push({ id, error: result.error });
                return;
            }
            const safeValue = String(result.value);

            // Per-row suppression (AC16, belt-and-suspenders): each setFieldValue also wrapped.
            Blockly.Events.disable();
            try {
                block.setFieldValue(safeValue, field);
            } finally {
                Blockly.Events.enable();
            }

            // Read-back hook (AC11): surface any clamp/mismatch after the write.
            const rb = readBack(block, safeValue, field);
            readBacks.push({ id, stored: rb.stored, mismatch: rb.mismatch });
            applied.push({ id, value: safeValue, mismatch: rb.mismatch });
        });
    } finally {
        // Always restore firing — even if a row threw.
        Blockly.Events.enable();
    }

    const banner = errors.length
        ? `${applied.length} applied, ${errors.length} skipped`
        : SUCCESS_BANNER;

    return { applied, errors, banner, readBacks };
};

export default applyEdits;
